import sortBy from 'lodash/sortBy'
import { useDeferredValue, useState } from 'react'

import { AstraMultiSelect, AstraParagraph, astraTwMerge, SelectOption, SelectOptionGroup } from '~/astra'
import { SearchText, useStatusOptions } from '~/components'
import { RecurringAssignmentTypes } from '~/storm'

import { allRecurringAssignmentRoomDays, allRecurringAssignmentStatusDays } from '../../shared'

type StatusOrRoomOption = {
    value: string
    type: 'status' | 'room'
}

export type RecurringAssignmentFilters = {
    practitioner: string[]
    statusOrRoom: StatusOrRoomOption[]
    search: string
}

type SetFilterCallback = <T extends keyof RecurringAssignmentFilters>(filter: T, value: RecurringAssignmentFilters[T]) => void

export const useRecurringAssignmentFilters = (recurringAssignments: RecurringAssignmentTypes) => {
    const [filters, setFilters] = useState<RecurringAssignmentFilters>({ practitioner: [], statusOrRoom: [], search: '' })
    const searchNeedle = useDeferredValue(filters.search.trim().toLowerCase())

    const handleSetFilter: SetFilterCallback = (filter, value) => {
        setFilters(prev => ({ ...prev, [filter]: value }))
    }

    const isAnyFilterActive = filters.practitioner.length > 0 || filters.statusOrRoom.length > 0 || searchNeedle.length > 0
    const filteredAssignments = recurringAssignments.filter(assignment => {
        if (!assignment.practitioner) return false
        if (!isAnyFilterActive) return true

        const practitionerMatch = filters.practitioner.length === 0 || filters.practitioner.some(f => f === String(assignment.practitioner_id))
        const statusRoomMatch =
            filters.statusOrRoom.length === 0 ||
            filters.statusOrRoom.some(f =>
                f.type === 'status'
                    ? allRecurringAssignmentStatusDays(assignment).some(s => s?.code === f.value)
                    : allRecurringAssignmentRoomDays(assignment).some(r => r?.room_code === f.value)
            )
        const searchMatch =
            searchNeedle.length === 0 ||
            assignment.title.toLowerCase().includes(searchNeedle) ||
            assignment.practitioner.short_name.toLowerCase().includes(searchNeedle)

        return practitionerMatch && statusRoomMatch && searchMatch
    })

    return {
        filters,
        handleSetFilter,
        filteredAssignments: sortBy(filteredAssignments, a => a.practitioner?.short_name),
        isAnyFilterActive,
        hiddenByFilterCount: recurringAssignments.length - filteredAssignments.length,
    }
}
type UseRecurringAssignmentFiltersReturn = ReturnType<typeof useRecurringAssignmentFilters>

type Props = {
    filters: RecurringAssignmentFilters
    recurringAssignments: RecurringAssignmentTypes
    onFilterChange: SetFilterCallback
    className?: string
}

export const RecurringAssignmentFilters = ({ filters, recurringAssignments, onFilterChange, className }: Props) => {
    const allStatusOptions = useStatusOptions()

    const practitionersOptionsMap = new Map<string, SelectOption<string>>()
    recurringAssignments.forEach(r => {
        if (r.practitioner) {
            practitionersOptionsMap.set(r.practitioner.short_name, {
                value: String(r.practitioner.id),
                label: r.practitioner.short_name,
            })
        }
    })

    const practitionerFilterOptions: SelectOption<string>[] = Array.from(practitionersOptionsMap.values()).sort((a, b) => a.label.localeCompare(b.label))

    const roomMap = new Map<string, SelectOption<StatusOrRoomOption>>()
    recurringAssignments
        .flatMap(r => allRecurringAssignmentRoomDays(r))
        .forEach(location => {
            if (location && location.room_code) {
                roomMap.set(location.room_code, { value: { value: location.room_code, type: 'room' }, label: location.room_code })
            }
        })
    const relevantRoomOptions = Array.from(roomMap.values()).sort((a, b) => a.label.localeCompare(b.label))

    const statusAndRoomOptions: SelectOptionGroup<StatusOrRoomOption>[] = [
        {
            label: 'Aktiviteter',
            options: allStatusOptions.map(s => ({
                label: s.label,
                value: { value: s.value, type: 'status' },
            })),
        },
        {
            label: 'Rom',
            options: relevantRoomOptions,
        },
    ]
    return (
        <div className={astraTwMerge('relative flex flex-col gap-2', className)}>
            <SearchText
                rawNeedle={filters.search}
                setNeedle={value => onFilterChange('search', value)}
                placeholder="Søk etter regler"
                data-test="recurring-assignments-search"
            />
            <div className="grid grid-cols-2 gap-x-3">
                <AstraMultiSelect
                    options={practitionerFilterOptions}
                    values={filters.practitioner}
                    onChange={options =>
                        onFilterChange(
                            'practitioner',
                            options.map(option => option.value)
                        )
                    }
                    placeholder="Kirurger"
                    data-test="recurring-assignments-surgeon-filter"
                />
                <AstraMultiSelect
                    options={statusAndRoomOptions}
                    values={filters.statusOrRoom}
                    disableGroupSelection
                    expandedGroups
                    onChange={options =>
                        onFilterChange(
                            'statusOrRoom',
                            options.map(option => option.value)
                        )
                    }
                    placeholder="Aktiviteter og rom"
                    data-test="recurring-assignments-status-filter"
                />
            </div>
        </div>
    )
}

export const pluralizeAssignmentCount = (count: number): string => (count === 1 ? 'regel' : 'regler')

export const RecurringAssignmentFilteredListMessage = ({
    filteredAssignments,
    hiddenByFilterCount,
}: Pick<UseRecurringAssignmentFiltersReturn, 'filteredAssignments' | 'hiddenByFilterCount'>) => {
    if (filteredAssignments.length === 0) {
        return <AstraParagraph className="text-center text-muted">Fant ingen regler med aktive filtre.</AstraParagraph>
    }
    if (hiddenByFilterCount > 0) {
        return (
            <AstraParagraph className="text-center text-muted">
                {`${hiddenByFilterCount} ${pluralizeAssignmentCount(hiddenByFilterCount)} er skjult av aktive filtre.`}
            </AstraParagraph>
        )
    }
    return null
}
